<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card v-if="!deleteCheck" class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="vehicle.id" class="text-h5">Update vehicle</h2>
              <h2 v-else class="text-h5">Add a vehicle</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <ValidationObserver ref="observer">
            <v-form class="modal-form">
              <ValidationProvider
                id="make"
                v-slot="{ errors }"
                name="make"
                rules="required"
              >
                <TextField
                  v-model.trim="vehicle.make"
                  :label="'Make*'"
                  :errors="errors"
                  :name="'make'"
                />
              </ValidationProvider>
              <ValidationProvider
                id="model"
                v-slot="{ errors }"
                name="model"
                rules="required"
              >
                <TextField
                  v-model.trim="vehicle.model"
                  :label="'Model*'"
                  :errors="errors"
                  :name="'model'"
                />
              </ValidationProvider>
              <ValidationProvider
                id="colour"
                v-slot="{ errors }"
                name="colour"
                rules="required"
              >
                <TextField
                  v-model.trim="vehicle.color"
                  :label="'Colour*'"
                  :errors="errors"
                  :name="'colour'"
                />
              </ValidationProvider>
            </v-form>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col v-if="vehicle.id" cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save'"
                  :small="true"
                  :callout="'right'"
                  :loading="buttonLoading"
                  @gradientButtonClicked="save"
                />
                <GradientButton
                  :title="'Remove Vehicle'"
                  :small="true"
                  :fill="false"
                  @gradientButtonClicked="deleteCheck = true"
                />
              </div>
            </v-col>
            <v-col v-else cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save And Continue'"
                  :small="true"
                  :callout="'right'"
                  @gradientButtonClicked="save"
                  :loading="buttonLoading"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <DeleteDialog
        v-if="deleteCheck"
        :content="deleteDialogContent"
        @delete="deleteVehicle"
        @dialogClosed="closeDialog"
      />
    </v-dialog>

    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import {
    CREATE_VEHICLE_MUTATION,
    UPDATE_VEHICLE_MUTATION,
    DELETE_VEHICLE_MUTATION,
  } from '../../constants/graphql/vehicles';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import DeleteDialog from '../common/DeleteDialog.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'VehicleDialog',
    components: {
      DeleteDialog,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      value: Boolean,
      vehicleToUpdate: Object,
    },
    data() {
      return {
        vehicle: {
          make: null,
          model: null,
          color: null,
        },
        deleteCheck: false,
      };
    },
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      deleteDialogContent() {
        const text =
          'Are you sure you want to remove this vehicle?<br><b>' +
          this.vehicle.displayName +
          '</b><br><br>' +
          this.deleteDonationWarning;
        return {
          title: 'Remove vehicle',
          text: text,
          buttonText: 'Yes, Remove Vehicle',
        };
      },
      deleteDonationWarning() {
        if (this.vehicle.specificDonation) {
          return '<b>Warning:</b> Donation of this asset to your desired beneficiaries will be deleted';
        } else {
          return '';
        }
      },
    },
    watch: {
      value: function (dialogOpen) {
        if (dialogOpen) {
          // using native JSON functions removes reactivity
          // so we can clone an object without mutating the original source
          this.vehicle = JSON.parse(JSON.stringify(this.vehicleToUpdate));
        }
      },
    },
    methods: {
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            if (this.vehicle.id) {
              this.updateVehicle();
            } else {
              this.createVehicle();
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createVehicle() {
        const vehicleInputDetails = {
          testatorId: this.testatorId,
          make: this.vehicle.make,
          model: this.vehicle.model,
          color: this.vehicle.color,
        };

        this.$apollo
          .mutate({
            mutation: CREATE_VEHICLE_MUTATION,
            variables: {
              vehicleInputDetails,
            },
            update: (cache, { data: { createVehicle } }) => {
              this.updateCacheAfterCreateVehicle(cache, createVehicle);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterCreateVehicle(cache, createVehicle) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.vehicles.push(createVehicle);

        cache.writeQuery({ ...query, data: data });
      },
      updateVehicle() {
        const vehicleInputDetails = {
          make: this.vehicle.make,
          model: this.vehicle.model,
          color: this.vehicle.color,
        };
        const id = this.vehicle.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_VEHICLE_MUTATION,
            variables: {
              id,
              vehicleInputDetails,
            },
            update: (cache, { data: { updateVehicle } }) => {
              this.updateCacheAfterUpdateVehicle(cache, updateVehicle);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterUpdateVehicle(cache, updateVehicle) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var vehicle = data.testator.vehicles.find(
          vehicle => vehicle.id == updateVehicle.id
        );
        var vehicle = updateVehicle;

        cache.writeQuery({ ...query, data: data });
      },
      deleteVehicle() {
        const id = this.vehicle.id;

        this.$apollo
          .mutate({
            mutation: DELETE_VEHICLE_MUTATION,
            variables: {
              id,
            },
            update: (cache, { data: { deleteVehicle } }) => {
              this.updateCacheAfterDeleteVehicle(cache, deleteVehicle);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterDeleteVehicle(cache, deleteVehicle) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var vehicles = data.testator.vehicles;
        var index = vehicles.findIndex(
          vehicle => vehicle.id == deleteVehicle.id
        );
        vehicles.splice(index, 1);

        cache.writeQuery({ ...query, data: data });
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.buttonLoading = false;
        this.vehicle = {};
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
        this.deleteCheck = false;
      },
    },
  };
</script>
<style lang="scss" scoped>
  .button-container {
    margin-bottom: 22px;
    * {
      margin-right: 15px;
    }
  }
</style>
